import Phaser from "phaser";
import audio from "./audio";

export const WORLD_W = 1600;
export const WORLD_H = 1200;

const A = "/assets/";

export default class GardenScene extends Phaser.Scene {
  constructor() {
    super("Garden");
  }

  preload() {
    const imgs = [
      "player_down", "player_up", "player_side",
      "eliza_down", "eliza_up", "eliza_side",
      "darcy_down", "darcy_up", "darcy_side",
      "fountain", "tree", "hedge", "bench", "rosebush", "fan",
      "grass_tile", "path_tile",
    ];
    imgs.forEach((k) => this.load.image(k, `${A}${k}.png`));
  }

  create() {
    this.controls = this.registry.get("controls");
    this.bus = this.registry.get("bus");
    this.quest = this.registry.get("quest") || "not_started";
    if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") window.__scene = this;
    this._fanTween = null;

    this.physics.world.setBounds(0, 0, WORLD_W, WORLD_H);
    this.cameras.main.setBounds(0, 0, WORLD_W, WORLD_H);
    this.cameras.main.setBackgroundColor("#7fae57");

    // ground (over-cover the world so no gaps ever show)
    const grass = this.add.tileSprite(-400, -400, WORLD_W + 800, WORLD_H + 800, "grass_tile")
      .setOrigin(0).setDepth(-20);
    grass.tileScaleX = 0.6;
    grass.tileScaleY = 0.6;
    // paths (cross + fountain ring)
    this.add.tileSprite(0, 530, WORLD_W, 150, "path_tile").setOrigin(0).setDepth(-15);
    this.add.tileSprite(720, 0, 150, WORLD_H, "path_tile").setOrigin(0).setDepth(-15);

    this.obstacles = this.physics.add.staticGroup();

    // helper: draw a visual image + an invisible static collision box
    const addObstacle = (key, x, y, dispW, boxW, boxH, boxOffY = 0) => {
      const img = this.add.image(x, y, key);
      const ar = img.width / img.height;
      img.setDisplaySize(dispW, dispW / ar);
      img.setDepth(y);
      const box = this.add.rectangle(x, y + boxOffY, boxW, boxH);
      this.physics.add.existing(box, true);
      this.obstacles.add(box);
      return img;
    };

    // fountain (center)
    addObstacle("fountain", 800, 600, 240, 200, 150, 20);
    // trees
    [[150, 180], [1450, 190], [150, 1050], [1450, 1050], [1150, 180], [430, 220]].forEach(
      ([x, y]) => addObstacle("tree", x, y, 200, 70, 45, 70)
    );
    // hedges forming little garden rooms
    const hedgeRow = (x0, y, n, dx = 110) => {
      for (let i = 0; i < n; i++) addObstacle("hedge", x0 + i * dx, y, 118, 108, 92);
    };
    hedgeRow(250, 360, 3);
    hedgeRow(1000, 360, 4);
    hedgeRow(1080, 820, 3);
    // benches
    addObstacle("bench", 620, 600, 150, 120, 46, 22);
    addObstacle("bench", 980, 600, 150, 120, 46, 22);
    // rose bushes
    [[250, 470], [1250, 340], [980, 900], [1180, 1050], [430, 1050]].forEach(
      ([x, y]) => addObstacle("rosebush", x, y, 120, 78, 62, 10)
    );

    // ---- player (invisible physics body + visual follower) ----
    this.playerHit = this.add.rectangle(320, 900, 42, 26);
    this.physics.add.existing(this.playerHit);
    this.playerHit.body.setCollideWorldBounds(true);

    this.player = this.add.sprite(320, 900, "player_down").setOrigin(0.5, 0.88);
    this.player.entity = { prefix: "player", targetH: 118, dir: null };
    this.setFacing(this.player, "down");

    this.physics.add.collider(this.playerHit, this.obstacles);

    // ---- NPCs ----
    this.npcGroup = this.physics.add.staticGroup();
    this.eliza = this.makeNpc("eliza", 620, 800, 122, [
      { x: 620, y: 780 }, { x: 620, y: 980 },
    ]);
    this.darcy = this.makeNpc("darcy", 1150, 760, 132, [
      { x: 1150, y: 760 }, { x: 1150, y: 470 },
    ]);
    this.physics.add.collider(this.playerHit, [this.eliza.hit, this.darcy.hit]);

    // ---- fan: always present & visible in the garden; pickup is gated by the
    // mission state (only offered when quest is "active"). Hidden only once collected.
    this.fan = this.add.image(1300, 1050, "fan").setDepth(2000);
    const far = this.fan.width / this.fan.height;
    this.fan.setDisplaySize(60, 60 / far);
    this.fanCollected = this.quest === "has_fan" || this.quest === "completed";
    if (this.fanCollected) this.fan.setVisible(false);
    else this.revealFan();

    // camera
    this.cameras.main.startFollow(this.playerHit, true, 0.12, 0.12);
    this.autoZoom();
    this.scale.on("resize", this.autoZoom, this);

    // input
    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys("W,A,S,D");

    // Drag-to-walk: the player grabs the character (finger or left mouse) and
    // steers it. Grabbing only starts when the press begins ON/near the sprite.
    this.dragging = false;
    this.input.on("pointerdown", (p) => {
      const ctrl = this.controls || {};
      if (ctrl.frozen) return;
      const d = Phaser.Math.Distance.Between(
        p.worldX, p.worldY, this.playerHit.x, this.playerHit.y
      );
      if (d <= 150) this.dragging = true;
    });
    const stopDrag = () => { this.dragging = false; };
    this.input.on("pointerup", stopDrag);
    this.input.on("pointerupoutside", stopDrag);
    this.input.on("gameout", stopDrag);

    // bus wiring
    this.lastNear = { eliza: false, darcy: false, fan: false };
    this.bus.emit("proximity", { nearEliza: false, nearDarcy: false, nearFan: false });

    this._onPickup = () => {
      if (this.fan.visible && !this.fanCollected) {
        this.fanCollected = true;
        this.tweens.add({
          targets: this.fan, y: this.fan.y - 40, alpha: 0, scale: this.fan.scale * 1.4,
          duration: 350, ease: "Quad.easeOut",
          onComplete: () => this.fan.setVisible(false),
        });
        this.bus.emit("picked");
      }
    };
    this._onQuest = (q) => {
      this.quest = q;
      if ((q === "not_started" || q === "active") && !this.fanCollected) {
        this.revealFan();
      } else if (q === "has_fan" || q === "completed") {
        this.fanCollected = true;
        this.fan.setVisible(false);
      }
    };
    this.bus.on("pickup", this._onPickup);
    this.bus.on("quest", this._onQuest);

    // Ask React for the current mission state now that the scene (re)exists,
    // so a scene restart never runs with a stale quest value.
    this.bus.emit("scene-ready");

    this.events.once("shutdown", () => {
      this.bus.off("pickup", this._onPickup);
      this.bus.off("quest", this._onQuest);
      this.scale.off("resize", this.autoZoom, this);
    });
  }

  autoZoom() {
    const w = this.scale.gameSize.width || window.innerWidth;
    const h = this.scale.gameSize.height || window.innerHeight;
    // cover the viewport so we never show outside the world
    const z = Phaser.Math.Clamp(Math.max(w / WORLD_W, h / WORLD_H), 0.4, 1.6);
    this.cameras.main.setZoom(z);
  }

  makeNpc(prefix, x, y, targetH, waypoints) {
    const hit = this.add.rectangle(x, y, 40, 24);
    this.physics.add.existing(hit);
    hit.body.setImmovable(true);
    const sprite = this.add.sprite(x, y, `${prefix}_down`).setOrigin(0.5, 0.88);
    sprite.entity = { prefix, targetH, dir: null };
    const npc = { hit, sprite, waypoints, wp: 1, frozen: false, entity: sprite.entity, bob: Math.random() * 6 };
    this.setFacing(sprite, "down");
    return npc;
  }

  revealFan() {
    this.fan.setVisible(true).setAlpha(1);
    if (this._fanTween && this._fanTween.isPlaying()) return;
    this._fanTween = this.tweens.add({
      targets: this.fan, y: this.fan.y - 8, duration: 900,
      yoyo: true, repeat: -1, ease: "Sine.easeInOut",
    });
  }

  setFacing(sprite, dir) {
    const e = sprite.entity;
    if (e.dir === dir) return;
    e.dir = dir;
    const key = e.prefix + (dir === "up" ? "_up" : dir === "down" ? "_down" : "_side");
    sprite.setTexture(key);
    sprite.setFlipX(dir === "right");
    const ar = sprite.frame.width / sprite.frame.height;
    sprite.setDisplaySize(e.targetH * ar, e.targetH);
    e.baseSX = sprite.scaleX;
    e.baseSY = sprite.scaleY;
  }

  dirFromVel(vx, vy) {
    if (Math.abs(vx) >= Math.abs(vy)) return vx > 0 ? "right" : "left";
    return vy > 0 ? "down" : "up";
  }

  animateChar(sprite, body, moving, t) {
    const e = sprite.entity;
    sprite.x = body.x;
    if (moving) {
      const s = Math.sin(t * 0.02 + (sprite.bobPhase || 0));
      sprite.scaleY = e.baseSY * (1 + 0.05 * s);
      sprite.scaleX = e.baseSX;
      sprite.y = body.y - Math.abs(s) * 4;
    } else {
      sprite.scaleY = e.baseSY;
      sprite.scaleX = e.baseSX;
      sprite.y = body.y;
    }
    sprite.setDepth(body.y);
  }

  update(time, delta) {
    const speed = 230;
    // gather input
    let ix = 0, iy = 0;
    const c = this.controls || {};
    if (!c.frozen) {
      const p = this.input.activePointer;
      if (this.dragging && p && p.isDown) {
        // Steer toward the finger/cursor, keeping the character slightly ABOVE
        // the touch point so the finger never hides the sprite. Fixed walking
        // speed (set below) => smooth, no teleporting.
        const dx = p.worldX - this.playerHit.x;
        const dy = (p.worldY - 55) - this.playerHit.y;
        const d = Math.hypot(dx, dy);
        if (d > 10) { ix = dx / d; iy = dy / d; }
      } else {
        this.dragging = false;
        if (this.cursors.left.isDown || this.wasd.A.isDown) ix -= 1;
        if (this.cursors.right.isDown || this.wasd.D.isDown) ix += 1;
        if (this.cursors.up.isDown || this.wasd.W.isDown) iy -= 1;
        if (this.cursors.down.isDown || this.wasd.S.isDown) iy += 1;
        const len = Math.hypot(ix, iy);
        if (len > 1) { ix /= len; iy /= len; }
      }
    }

    const body = this.playerHit.body;
    body.setVelocity(ix * speed, iy * speed);
    const moving = Math.abs(ix) > 0.05 || Math.abs(iy) > 0.05;
    if (moving) { this.setFacing(this.player, this.dirFromVel(ix, iy)); audio.footstep(); }
    this.animateChar(this.player, this.playerHit, moving, time);

    // NPCs (both pause & turn toward the player when approached)
    this.updateNpc(this.darcy, time, true);
    this.updateNpc(this.eliza, time, true);

    // proximity
    const de = Phaser.Math.Distance.Between(this.playerHit.x, this.playerHit.y, this.eliza.hit.x, this.eliza.hit.y);
    const dd = Phaser.Math.Distance.Between(this.playerHit.x, this.playerHit.y, this.darcy.hit.x, this.darcy.hit.y);
    const df = this.fan.visible
      ? Phaser.Math.Distance.Between(this.playerHit.x, this.playerHit.y, this.fan.x, this.fan.y)
      : 9999;
    const nearEliza = de < 135;
    const nearDarcy = dd < 135;
    const nearFan = df < 95;
    if (
      nearEliza !== this.lastNear.eliza ||
      nearDarcy !== this.lastNear.darcy ||
      nearFan !== this.lastNear.fan
    ) {
      this.lastNear = { eliza: nearEliza, darcy: nearDarcy, fan: nearFan };
      this.bus.emit("proximity", { nearEliza, nearDarcy, nearFan });
    }
  }

  updateNpc(npc, time, canFreeze) {
    const near = canFreeze &&
      Phaser.Math.Distance.Between(this.playerHit.x, this.playerHit.y, npc.hit.x, npc.hit.y) < 135;
    let moving = false;
    if (near) {
      npc.hit.body.setVelocity(0, 0);
      const dx = this.playerHit.x - npc.hit.x;
      const dy = this.playerHit.y - npc.hit.y;
      this.setFacing(npc.sprite, this.dirFromVel(dx, dy));
    } else {
      const target = npc.waypoints[npc.wp];
      const dx = target.x - npc.hit.x;
      const dy = target.y - npc.hit.y;
      const d = Math.hypot(dx, dy);
      if (d < 6) {
        npc.wp = (npc.wp + 1) % npc.waypoints.length;
        npc.hit.body.setVelocity(0, 0);
      } else {
        const sp = 60;
        npc.hit.body.setVelocity((dx / d) * sp, (dy / d) * sp);
        this.setFacing(npc.sprite, this.dirFromVel(dx, dy));
        moving = true;
      }
    }
    npc.sprite.bobPhase = npc.bob;
    this.animateChar(npc.sprite, npc.hit, moving || near, time);
    // gentle idle breathing when frozen
    if (near && !moving) {
      npc.sprite.scaleY = npc.entity.baseSY * (1 + 0.02 * Math.sin(time * 0.005));
    }
  }
}
